import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

export default function BookingDialog({ open, onOpenChange, tour }) {
  const groupDeps = tour.package_type === "GROUP" ? (tour.departures || []) : [];
  const [form, setForm] = useState({
    client_name: "", phone: "", email: "", pax: 2, travel_date: "", departure_id: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const selectedDep = groupDeps.find((d) => d.id === form.departure_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.phone) { toast.error("Please enter name and phone"); return; }
    setLoading(true);
    try {
      await api.post("/enquiries", {
        name: form.client_name, phone: form.phone, email: form.email,
        destination: tour.destination || "", tour_slug: tour.slug, package_name: tour.title,
        travel_date: selectedDep?.date || form.travel_date, pax: Number(form.pax) || 1,
        source: "Website", message: form.notes,
      });
      toast.success("Enquiry received! Our travel experts will reach out shortly.");
      onOpenChange(false);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const input = "w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-navy outline-none focus:border-brandorange";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="booking-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display text-2xl text-navy">Enquire about {tour.title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="booking-name" className={input} placeholder="Full Name *" value={form.client_name} onChange={set("client_name")} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="booking-phone" className={input} placeholder="Phone *" value={form.phone} onChange={set("phone")} />
            <input data-testid="booking-email" className={input} placeholder="Email" value={form.email} onChange={set("email")} />
          </div>
          {tour.package_type === "GROUP" ? (
            <select data-testid="booking-departure" className={input} value={form.departure_id} onChange={set("departure_id")}>
              <option value="">Preferred Departure Date</option>
              {groupDeps.map((d) => {
                const left = d.seats_total - d.seats_booked;
                return <option key={d.id} value={d.id} disabled={left <= 0}>{new Date(d.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} — {left > 0 ? `${left} seats left` : "Sold Out"}</option>;
              })}
            </select>
          ) : (
            <input data-testid="booking-date" type="date" className={input} value={form.travel_date} onChange={set("travel_date")} />
          )}
          <input data-testid="booking-pax" type="number" min="1" className={input} placeholder="Number of Travellers" value={form.pax} onChange={set("pax")} />
          <textarea data-testid="booking-notes" rows={2} className={input} placeholder="Tell us your preferences (dates, budget, requests)..." value={form.notes} onChange={set("notes")} />
          <button data-testid="booking-submit" disabled={loading} className="btn-gradient flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-bold disabled:opacity-70">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send Enquiry"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
